import { ActionOptions } from 'app/modules/wallforming/components/input-binding/types/actionOptions';
import { InputActionDefinition } from 'app/modules/wallforming/components/input-binding/types/inputActionDefinition';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputCategory } from 'app/modules/wallforming/components/input-binding/types/inputCategory';
import { InputContext } from 'app/modules/wallforming/components/input-binding/types/inputContext';
import { InputDevice } from 'app/modules/wallforming/components/input-binding/types/inputDevice';
import { InputFocus } from 'app/modules/wallforming/components/input-binding/types/inputFocus';
import { InputMetadata } from 'app/modules/wallforming/components/input-binding/types/inputMetadata';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import { MouseButton } from 'app/modules/wallforming/components/input-binding/types/mouseButton';
import { ReservedInput } from 'app/modules/wallforming/components/input-binding/types/reservedInput';
import { ALL_INPUT_CONTEXTS } from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';

export const INPUT_BINDING_SLOTS = 2;

export const inputCategories: Record<InputCategory, InputMetadata> = {
    [InputCategory.General]: { labelKey: 'labelKeyBindingCategoryGeneral', icon: 'settings' },
    [InputCategory.Camera]: { labelKey: 'labelKeyBindingCategoryCamera', icon: 'videocam' },
    [InputCategory.Poi]: { labelKey: 'labelKeyBindingCategoryNavigation', icon: 'location_on' },
    [InputCategory.Article]: { labelKey: 'labelKeyBindingCategoryArticles', icon: 'deployed_code' },
    [InputCategory.Selection]: { labelKey: 'labelKeyBindingCategorySelection', icon: 'select' },
    [InputCategory.Placement]: { labelKey: 'labelKeyBindingCategoryPlacement', icon: 'auto_fix' }
};

export const inputContexts: Record<InputContext, InputMetadata> = {
    [InputContext.Placement]: { labelKey: 'labelInputContextPlacement', icon: 'arrow_selector_tool' },
    [InputContext.Navigation]: { labelKey: 'labelInputContextNavigation', icon: 'location_on' },
    [InputContext.Transformation]: { labelKey: 'labelInputContextTransformation', icon: 'transform' },
    [InputContext.Configuration]: { labelKey: 'labelInputContextConfiguration', icon: 'manufacturing' },
    [InputContext.View]: { labelKey: 'labelInputContextView', icon: 'visibility' }
};

const createCommand = (options: ActionOptions): InputActionDefinition => ({
    devices: [InputDevice.Keyboard, InputDevice.Mouse],
    triggers: [InputTrigger.Click, InputTrigger.DoubleClick],
    slots: INPUT_BINDING_SLOTS,
    focus: InputFocus.NoTextInput,
    ...options
});

/** Continuous pointer interaction queried by its owning system instead of being dispatched as a createCommand. */
const createGesture = (options: ActionOptions): InputActionDefinition =>
    createCommand({
        devices: [InputDevice.Mouse],
        triggers: [InputTrigger.Drag],
        slots: 1,
        scope: 'scene',
        ...options
    });

const { Placement, Navigation, Transformation, Configuration, View } = InputContext;
const ARTICLE_CONTEXTS = [Placement, Transformation, View] as const;
const ARTICLE_EDIT_CONTEXTS = [Placement, Transformation] as const;
const NON_CONFIGURATION_CONTEXTS = [Placement, Navigation, Transformation, View] as const;

/** Single source of truth for every bindable action; insertion order is the display order. */
export const inputActions: Record<InputActionId, InputActionDefinition> = {
    [InputActionId.GeneralOpenKeyBindingPanel]: createCommand({
        copilot: { edits: false },
        category: InputCategory.General,
        labelKey: 'labelInputActionOpenKeyBindingPanel',
        contexts: ALL_INPUT_CONTEXTS,
        capture: true
    }),
    [InputActionId.GeneralCancel]: createCommand({
        copilot: { edits: false },
        category: InputCategory.General,
        labelKey: 'labelInputActionCancel',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Keyboard],
        focus: InputFocus.Any,
        capture: true,
        allowInInputUI: true
    }),
    [InputActionId.GeneralOpenProjects]: createCommand({
        copilot: { edits: false },
        category: InputCategory.General,
        labelKey: 'labelInputActionOpenProjects',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Keyboard],
        focus: InputFocus.Any
    }),
    [InputActionId.GeneralSave]: createCommand({
        category: InputCategory.General,
        labelKey: 'labelInputActionSave',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Keyboard],
        focus: InputFocus.Any
    }),
    [InputActionId.GeneralToggleViewMode]: createCommand({
        copilot: { edits: false },
        category: InputCategory.General,
        labelKey: 'labelInputActionToggleViewMode',
        contexts: ALL_INPUT_CONTEXTS
    }),
    [InputActionId.GeneralToggleCopilot]: createCommand({
        category: InputCategory.General,
        labelKey: 'labelInputActionToggleCopilot',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Keyboard],
        focus: InputFocus.Any,
        capture: true,
        allowInInputUI: true
    }),

    [InputActionId.CameraCenter]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Camera,
        labelKey: 'labelInputActionCameraCenter',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Keyboard, InputDevice.Mouse],
        triggers: [InputTrigger.DoubleClick],
        mouseButtons: [MouseButton.Left, MouseButton.Middle, MouseButton.Right],
        focus: InputFocus.Scene,
        capture: true
    }),
    [InputActionId.CameraLockToggle]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Camera,
        labelKey: 'labelInputActionCameraLock',
        contexts: ALL_INPUT_CONTEXTS,
        focus: InputFocus.Scene,
        capture: true
    }),
    [InputActionId.CameraUnlockToggle]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Camera,
        labelKey: 'labelInputActionCameraUnlock',
        contexts: ALL_INPUT_CONTEXTS,
        focus: InputFocus.Scene,
        capture: true
    }),
    [InputActionId.CameraRotate]: createGesture({
        category: InputCategory.Camera,
        labelKey: 'labelInputActionCameraRotate',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Mouse, InputDevice.Wheel],
        mouseButtons: [MouseButton.Left, MouseButton.Middle, MouseButton.Right]
    }),
    [InputActionId.CameraTruck]: createGesture({
        category: InputCategory.Camera,
        labelKey: 'labelInputActionCameraTruck',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Mouse, InputDevice.Wheel],
        mouseButtons: [MouseButton.Left, MouseButton.Middle, MouseButton.Right]
    }),
    [InputActionId.CameraZoom]: createGesture({
        category: InputCategory.Camera,
        labelKey: 'labelInputActionCameraZoom',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Mouse, InputDevice.Wheel],
        triggers: [InputTrigger.Drag],
        mouseButtons: [MouseButton.Left, MouseButton.Middle, MouseButton.Right]
    }),

    [InputActionId.PoiNavigationModeToggle]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiNavigationToggle',
        contexts: ALL_INPUT_CONTEXTS,
        focus: InputFocus.Scene,
        capture: true
    }),
    [InputActionId.PoiNavigationTemporary]: createCommand({
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiNavigationTemporary',
        contexts: ALL_INPUT_CONTEXTS,
        devices: [InputDevice.Keyboard],
        capture: true
    }),
    [InputActionId.PoiPrevious]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiPrevious',
        contexts: NON_CONFIGURATION_CONTEXTS
    }),
    [InputActionId.PoiNext]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiNext',
        contexts: NON_CONFIGURATION_CONTEXTS
    }),
    [InputActionId.PoiSwitchType]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiSwitchType',
        contexts: ALL_INPUT_CONTEXTS,
        capture: true
    }),
    [InputActionId.PoiSwitchTypeReversed]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiSwitchTypeReversed',
        contexts: ALL_INPUT_CONTEXTS,
        capture: true
    }),
    [InputActionId.PoiToggleCameraRealignment]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiToggleCameraRealignment',
        contexts: ALL_INPUT_CONTEXTS,
        capture: true
    }),
    [InputActionId.PoiSelect]: createGesture({
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiSelect',
        contexts: [Navigation, Configuration, View],
        triggers: [InputTrigger.Click, InputTrigger.DoubleClick]
    }),
    [InputActionId.PoiSelectAndAlign]: createGesture({
        category: InputCategory.Poi,
        labelKey: 'labelInputActionPoiSelectAndAlign',
        contexts: [Navigation, Configuration, View],
        triggers: [InputTrigger.Click, InputTrigger.DoubleClick]
    }),

    [InputActionId.ArticleCopy]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleCopy',
        contexts: ARTICLE_EDIT_CONTEXTS,
        focus: InputFocus.Scene
    }),
    [InputActionId.ArticleCut]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleCut',
        contexts: ARTICLE_EDIT_CONTEXTS,
        focus: InputFocus.Scene
    }),
    [InputActionId.ArticlePaste]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticlePaste',
        contexts: ARTICLE_EDIT_CONTEXTS,
        focus: InputFocus.Scene
    }),
    [InputActionId.ArticleSelectAll]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleSelectAll',
        contexts: ARTICLE_CONTEXTS,
        focus: InputFocus.Scene
    }),
    [InputActionId.ArticleDelete]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleDelete',
        contexts: ARTICLE_EDIT_CONTEXTS,
        focus: InputFocus.Scene
    }),
    [InputActionId.ArticleTransform]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleTransform',
        contexts: ARTICLE_EDIT_CONTEXTS,
        focus: InputFocus.Any
    }),
    [InputActionId.ArticleRotate]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleRotate',
        contexts: ARTICLE_EDIT_CONTEXTS
    }),
    [InputActionId.ArticleQuickReplacement]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Article,
        labelKey: 'labelInputActionPanelQuickReplacement',
        contexts: [...ARTICLE_EDIT_CONTEXTS, Navigation, Configuration],
        focus: InputFocus.NoTextInput
    }),
    [InputActionId.ArticleQuickReplacementLeft]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionPanelQuickReplacementLeft',
        contexts: [Configuration],
        devices: [InputDevice.Keyboard],
        allowInInputUI: true
    }),
    [InputActionId.ArticleQuickReplacementRight]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionPanelQuickReplacementRight',
        contexts: [Configuration],
        devices: [InputDevice.Keyboard],
        allowInInputUI: true
    }),
    [InputActionId.ArticleQuickReplacementUp]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionPanelQuickReplacementUp',
        contexts: [Configuration],
        devices: [InputDevice.Keyboard],
        allowInInputUI: true
    }),
    [InputActionId.ArticleQuickReplacementDown]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionPanelQuickReplacementDown',
        contexts: [Configuration],
        devices: [InputDevice.Keyboard],
        allowInInputUI: true
    }),
    [InputActionId.ArticleQuickReplacementInwardModifier]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionPanelQuickReplacementInwardModifier',
        contexts: [Configuration],
        devices: [InputDevice.Keyboard],
        triggers: [],
        slots: 1,
        keyboardModifierOnly: true
    }),
    [InputActionId.ArticleGroup]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleGroup',
        contexts: ARTICLE_EDIT_CONTEXTS
    }),
    [InputActionId.ArticleUngroup]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleUngroup',
        contexts: ARTICLE_EDIT_CONTEXTS
    }),
    [InputActionId.ArticleShowContextMenu]: createCommand({
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleContextMenu',
        contexts: ARTICLE_EDIT_CONTEXTS,
        devices: [InputDevice.Mouse],
        triggers: [InputTrigger.Click],
        mouseButtons: [MouseButton.Right],
        scope: 'scene'
    }),
    [InputActionId.ArticleCatalogueDrag]: createGesture({
        category: InputCategory.Article,
        labelKey: 'labelInputActionArticleCatalogueDrag',
        contexts: [...ARTICLE_EDIT_CONTEXTS, Configuration],
        scope: 'catalogue'
    }),

    [InputActionId.SelectionBoxFullyEnclosed]: createGesture({
        category: InputCategory.Selection,
        labelKey: 'labelBoxSelectionFull',
        contexts: [Placement, View],
        scope: 'scene.leftToRight'
    }),
    [InputActionId.SelectionBoxPartiallyEnclosed]: createGesture({
        category: InputCategory.Selection,
        labelKey: 'labelBoxSelectionPartial',
        contexts: [Placement, View],
        scope: 'scene.rightToLeft'
    }),
    [InputActionId.SelectionBoxRecursive]: createGesture({
        category: InputCategory.Selection,
        labelKey: 'labelBoxSelectionRecursive',
        contexts: [Placement, View],
        modifierOnly: true
    }),

    [InputActionId.PlacementExecute]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Placement,
        labelKey: 'labelInputActionPlacementExecute',
        contexts: [Placement, Navigation],
        devices: [InputDevice.Keyboard],
        scope: 'placement.execute'
    }),
    [InputActionId.PlacementApplySelectedSolution]: createCommand({
        copilot: { edits: true },
        category: InputCategory.Placement,
        labelKey: 'labelInputActionPlacementApplySolution',
        contexts: [Placement, Navigation],
        devices: [InputDevice.Keyboard],
        focus: InputFocus.Any,
        scope: 'placement.solution'
    }),
    [InputActionId.PlacementListNavigationUp]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Placement,
        labelKey: 'labelInputActionPlacementListUp',
        contexts: NON_CONFIGURATION_CONTEXTS,
        devices: [InputDevice.Keyboard],
        capture: true
    }),
    [InputActionId.PlacementListNavigationDown]: createCommand({
        copilot: { edits: false },
        category: InputCategory.Placement,
        labelKey: 'labelInputActionPlacementListDown',
        contexts: NON_CONFIGURATION_CONTEXTS,
        devices: [InputDevice.Keyboard],
        capture: true
    })
};

export const inputActionIds = Object.keys(inputActions) as InputActionId[];

const digits = Array.from({ length: 9 }, (_, index) => index + 1);

/** Inputs handled outside the binding system; shown read-only and protected against conflicts. */
export const reservedInputs: readonly ReservedInput[] = [
    {
        id: 'placement-select-type',
        category: InputCategory.Placement,
        labelKey: 'labelInputActionPlacementSelectType',
        displayLabel: '1 - 9',
        contexts: ALL_INPUT_CONTEXTS,
        chords: digits.flatMap((digit) => [`Digit${digit}`, `Numpad${digit}`])
    }
];
